import React, { useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';

import {
  getFontFamilies,
  loadFontFace,
  FontFaceType,
  processFontFamily,
  generateTailwindVariables,
  generateVariantComponents,
  TAILWIND_CSS_CLASS,
} from './theme/themeHelpers';
import { Helmet } from 'react-helmet';
import { PageTreeNode } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import '@tailwindplus/elements';

interface PageProps {
  children: React.ReactNode;
  treeNode: PageTreeNode;
  editorClassName?: string;
}

export const Page = React.forwardRef<HTMLDivElement, PageProps>(
  ({ children, treeNode, editorClassName, ...props }, ref) => {
    const { pageTitle, favicon, pageDescription, ...themeProps } = treeNode.data;
    const loadedFontsRef = useRef<FontFaceType[]>([]);

    const fontFamilies = useMemo(() => getFontFamilies(themeProps), [themeProps]);

    useEffect(() => {
      const fontFaces = processFontFamily(fontFamilies);

      fontFaces.forEach(async (font) => {
        const alreadyLoaded = loadedFontsRef.current.some(
          (loaded) =>
            loaded.fontFamily === font.fontFamily && loaded.fontWeight === font.fontWeight && loaded.src === font.src,
        );

        if (!alreadyLoaded && font.src) {
          await loadFontFace(font.fontFamily, font.fontWeight, font.src, font.fontStyle);
          loadedFontsRef.current.push(font);
        }
      });
    }, [fontFamilies]);

    // Generate CSS variables, utilities, and variant components from flattened theme props
    const cssContent = useMemo(() => {
      const tailwindVars = generateTailwindVariables(themeProps);
      const variantComponents = generateVariantComponents(themeProps);

      return {
        variables: tailwindVars,
        components: variantComponents,
      };
    }, [themeProps]);

    return (
      <>
        <Helmet>
          <style type="text/tailwindcss">{`
            @theme {
              ${Object.entries(cssContent.variables)
                .map(([key, value]) => `${key}: ${value};`)
                .join('\n          ')}
            }
            ${cssContent.components}
          `}</style>
        </Helmet>

        <main>
          <div ref={ref} {...props} className={cx(TAILWIND_CSS_CLASS, editorClassName)}>
            {children}
          </div>
        </main>
      </>
    );
  },
);

Page.displayName = 'Page';
